import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress, Box, Paper, Typography, Button } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRoles = [] }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(135deg, #f8faff 0%, #f1f5ff 50%, #e0e7ff 100%)',
        }}
      >
        <CircularProgress 
          size={60}
          sx={{
            color: '#6366f1',
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            },
          }}
        />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has required role
  if (requiredRoles.length > 0 && user && !requiredRoles.includes(user.role)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(135deg, #f8faff 0%, #f1f5ff 50%, #e0e7ff 100%)',
          p: 3,
        }}
      >
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            maxWidth: 400,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(99, 102, 241, 0.2)',
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(99, 102, 241, 0.1)',
          }}
        >
          <LockIcon sx={{ fontSize: 60, color: '#ff6b6b', mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ color: '#333', fontWeight: 600 }}>
            Access Denied
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', mb: 3 }}>
            You don't have permission to access this page. Required roles: {requiredRoles.join(', ')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
            Your current role: <strong>{user.role}</strong>
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.history.back()}
            sx={{
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
              },
            }}
          >
            Go Back
          </Button>
        </Paper>
      </Box>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;